// Google Fonts catalog for the design editor — checked-in (no runtime
// metadata API call; the css2 endpoint used by the loader is keyless).
// Curated ~140 popular families by category. The loader tolerates weight
// mismatches (retries without weight spec), so `weights` here is advisory —
// it powers the weight dropdown, not correctness.
//
// MUST include every family the template generator steers toward
// (template_generator/api/src/prompts/html-generation.txt): Playfair Display,
// Open Sans, Montserrat, Bebas Neue, Roboto, Lato, Cormorant Garamond —
// templates silently fell back before this catalog existed.
//
// The 8 bundled OFL faces (editorFonts.js) are pinned as "Teacher picks" and
// load from disk; system staples never hit the network.

import { EDITOR_FONTS } from '../editorFonts';

const W_REG = [400, 700];
const W_FULL = [300, 400, 500, 600, 700, 800, 900];
const W_MED = [400, 500, 600, 700];
const W_ONE = [400];

// category: Sans | Serif | Display | Script | Handwritten | Mono | Fun
const GOOGLE_FONTS = [
  // ---- Sans-serif workhorses ----
  { family: 'Open Sans', category: 'Sans', weights: W_FULL },
  { family: 'Roboto', category: 'Sans', weights: [100, 300, 400, 500, 700, 900] },
  { family: 'Lato', category: 'Sans', weights: [300, 400, 700, 900] },
  { family: 'Montserrat', category: 'Sans', weights: W_FULL },
  { family: 'Poppins', category: 'Sans', weights: W_FULL },
  { family: 'Inter', category: 'Sans', weights: W_FULL },
  { family: 'Raleway', category: 'Sans', weights: W_FULL },
  { family: 'Nunito Sans', category: 'Sans', weights: W_FULL },
  { family: 'Work Sans', category: 'Sans', weights: W_FULL },
  { family: 'Rubik', category: 'Sans', weights: W_MED },
  { family: 'Mulish', category: 'Sans', weights: W_FULL },
  { family: 'Quicksand', category: 'Sans', weights: [300, 400, 500, 600, 700] },
  { family: 'Josefin Sans', category: 'Sans', weights: W_MED },
  { family: 'Barlow', category: 'Sans', weights: W_FULL },
  { family: 'Manrope', category: 'Sans', weights: W_FULL },
  { family: 'Karla', category: 'Sans', weights: W_MED },
  { family: 'Outfit', category: 'Sans', weights: W_FULL },
  { family: 'DM Sans', category: 'Sans', weights: W_MED },
  { family: 'Cabin', category: 'Sans', weights: W_MED },
  { family: 'Source Sans 3', category: 'Sans', weights: W_FULL },
  { family: 'PT Sans', category: 'Sans', weights: W_REG },
  { family: 'Oxygen', category: 'Sans', weights: [300, 400, 700] },
  { family: 'Assistant', category: 'Sans', weights: W_MED },
  { family: 'Figtree', category: 'Sans', weights: W_FULL },
  { family: 'Urbanist', category: 'Sans', weights: W_FULL },
  { family: 'Plus Jakarta Sans', category: 'Sans', weights: W_FULL },
  { family: 'Exo 2', category: 'Sans', weights: W_FULL },
  { family: 'Fira Sans', category: 'Sans', weights: W_MED },
  { family: 'Archivo', category: 'Sans', weights: W_FULL },
  { family: 'Jost', category: 'Sans', weights: W_MED },

  // ---- Serifs ----
  { family: 'Cormorant Garamond', category: 'Serif', weights: [300, 400, 500, 600, 700] },
  { family: 'Merriweather', category: 'Serif', weights: [300, 400, 700, 900] },
  { family: 'Lora', category: 'Serif', weights: W_MED },
  { family: 'PT Serif', category: 'Serif', weights: W_REG },
  { family: 'Libre Baskerville', category: 'Serif', weights: W_REG },
  { family: 'Crimson Text', category: 'Serif', weights: [400, 600, 700] },
  { family: 'EB Garamond', category: 'Serif', weights: W_MED },
  { family: 'Bitter', category: 'Serif', weights: W_FULL },
  { family: 'Domine', category: 'Serif', weights: W_MED },
  { family: 'Source Serif 4', category: 'Serif', weights: W_MED },
  { family: 'Noto Serif', category: 'Serif', weights: W_REG },
  { family: 'Vollkorn', category: 'Serif', weights: W_MED },
  { family: 'Cardo', category: 'Serif', weights: W_REG },
  { family: 'Spectral', category: 'Serif', weights: W_MED },
  { family: 'Zilla Slab', category: 'Serif', weights: W_MED },
  { family: 'Arvo', category: 'Serif', weights: W_REG },
  { family: 'Rokkitt', category: 'Serif', weights: W_MED },
  { family: 'Slabo 27px', category: 'Serif', weights: W_ONE },
  { family: 'Roboto Slab', category: 'Serif', weights: W_FULL },
  { family: 'Josefin Slab', category: 'Serif', weights: W_MED },

  // ---- Display / poster headline faces ----
  { family: 'Oswald', category: 'Display', weights: [300, 400, 500, 600, 700] },
  { family: 'Archivo Black', category: 'Display', weights: W_ONE },
  { family: 'Alfa Slab One', category: 'Display', weights: W_ONE },
  { family: 'Righteous', category: 'Display', weights: W_ONE },
  { family: 'Bungee', category: 'Display', weights: W_ONE },
  { family: 'Titan One', category: 'Display', weights: W_ONE },
  { family: 'Luckiest Guy', category: 'Display', weights: W_ONE },
  { family: 'Bangers', category: 'Display', weights: W_ONE },
  { family: 'Lilita One', category: 'Display', weights: W_ONE },
  { family: 'Passion One', category: 'Display', weights: [400, 700, 900] },
  { family: 'Fjalla One', category: 'Display', weights: W_ONE },
  { family: 'Russo One', category: 'Display', weights: W_ONE },
  { family: 'Staatliches', category: 'Display', weights: W_ONE },
  { family: 'Abril Fatface', category: 'Display', weights: W_ONE },
  { family: 'Ultra', category: 'Display', weights: W_ONE },
  { family: 'Shrikhand', category: 'Display', weights: W_ONE },
  { family: 'Rye', category: 'Display', weights: W_ONE },
  { family: 'Monoton', category: 'Display', weights: W_ONE },
  { family: 'Creepster', category: 'Display', weights: W_ONE },
  { family: 'Special Elite', category: 'Display', weights: W_ONE },
  { family: 'Graduate', category: 'Display', weights: W_ONE },
  { family: 'Bowlby One SC', category: 'Display', weights: W_ONE },
  { family: 'Changa One', category: 'Display', weights: W_ONE },
  { family: 'Secular One', category: 'Display', weights: W_ONE },
  { family: 'Paytone One', category: 'Display', weights: W_ONE },
  { family: 'Sigmar One', category: 'Display', weights: W_ONE },
  { family: 'Concert One', category: 'Display', weights: W_ONE },
  { family: 'Squada One', category: 'Display', weights: W_ONE },
  { family: 'Teko', category: 'Display', weights: W_MED },
  { family: 'Anton SC', category: 'Display', weights: W_ONE },

  // ---- Script / elegant ----
  { family: 'Dancing Script', category: 'Script', weights: W_MED },
  { family: 'Great Vibes', category: 'Script', weights: W_ONE },
  { family: 'Satisfy', category: 'Script', weights: W_ONE },
  { family: 'Lobster', category: 'Script', weights: W_ONE },
  { family: 'Lobster Two', category: 'Script', weights: W_REG },
  { family: 'Sacramento', category: 'Script', weights: W_ONE },
  { family: 'Allura', category: 'Script', weights: W_ONE },
  { family: 'Parisienne', category: 'Script', weights: W_ONE },
  { family: 'Yellowtail', category: 'Script', weights: W_ONE },
  { family: 'Cookie', category: 'Script', weights: W_ONE },
  { family: 'Courgette', category: 'Script', weights: W_ONE },
  { family: 'Kaushan Script', category: 'Script', weights: W_ONE },
  { family: 'Alex Brush', category: 'Script', weights: W_ONE },
  { family: 'Tangerine', category: 'Script', weights: W_REG },
  { family: 'Pinyon Script', category: 'Script', weights: W_ONE },
  { family: 'Mr Dafoe', category: 'Script', weights: W_ONE },
  { family: 'Norican', category: 'Script', weights: W_ONE },
  { family: 'Grand Hotel', category: 'Script', weights: W_ONE },

  // ---- Handwritten / kid-friendly ----
  { family: 'Caveat', category: 'Handwritten', weights: W_MED },
  { family: 'Shadows Into Light', category: 'Handwritten', weights: W_ONE },
  { family: 'Indie Flower', category: 'Handwritten', weights: W_ONE },
  { family: 'Amatic SC', category: 'Handwritten', weights: W_REG },
  { family: 'Kalam', category: 'Handwritten', weights: [300, 400, 700] },
  { family: 'Handlee', category: 'Handwritten', weights: W_ONE },
  { family: 'Gochi Hand', category: 'Handwritten', weights: W_ONE },
  { family: 'Coming Soon', category: 'Handwritten', weights: W_ONE },
  { family: 'Schoolbell', category: 'Handwritten', weights: W_ONE },
  { family: 'Gloria Hallelujah', category: 'Handwritten', weights: W_ONE },
  { family: 'Architects Daughter', category: 'Handwritten', weights: W_ONE },
  { family: 'Homemade Apple', category: 'Handwritten', weights: W_ONE },
  { family: 'Rock Salt', category: 'Handwritten', weights: W_ONE },
  { family: 'Permanent Marker', category: 'Handwritten', weights: W_ONE },
  { family: 'Reenie Beanie', category: 'Handwritten', weights: W_ONE },
  { family: 'Nanum Pen Script', category: 'Handwritten', weights: W_ONE },
  { family: 'Sriracha', category: 'Handwritten', weights: W_ONE },
  { family: 'Mali', category: 'Handwritten', weights: W_MED },

  // ---- Fun / rounded (kid-appeal) ----
  { family: 'Baloo 2', category: 'Fun', weights: W_MED },
  { family: 'Chewy', category: 'Fun', weights: W_ONE },
  { family: 'Boogaloo', category: 'Fun', weights: W_ONE },
  { family: 'Sniglet', category: 'Fun', weights: [400, 800] },
  { family: 'Patrick Hand SC', category: 'Fun', weights: W_ONE },
  { family: 'Delius', category: 'Fun', weights: W_ONE },
  { family: 'Comic Neue', category: 'Fun', weights: [300, 400, 700] },
  { family: 'Chelsea Market', category: 'Fun', weights: W_ONE },
  { family: 'Grandstander', category: 'Fun', weights: W_MED },
  { family: 'Salsa', category: 'Fun', weights: W_ONE },
  { family: 'Modak', category: 'Fun', weights: W_ONE },
  { family: 'Ranchers', category: 'Fun', weights: W_ONE },
  { family: 'Cherry Cream Soda', category: 'Fun', weights: W_ONE },
  { family: 'Mochiy Pop One', category: 'Fun', weights: W_ONE },

  // ---- Mono ----
  { family: 'Roboto Mono', category: 'Mono', weights: W_REG },
  { family: 'Source Code Pro', category: 'Mono', weights: W_REG },
  { family: 'Space Mono', category: 'Mono', weights: W_REG },
  { family: 'IBM Plex Mono', category: 'Mono', weights: W_REG },
  { family: 'Courier Prime', category: 'Mono', weights: W_REG },

  // ---- Accessibility ----
  { family: 'Atkinson Hyperlegible', category: 'Sans', weights: W_REG },
  { family: 'Andika', category: 'Sans', weights: W_REG },
];

// Merge shape: { family, category, weights, source: 'bundled'|'system'|'google' }
const bundledSet = EDITOR_FONTS.filter((f) => f.bundled).map((f) => ({
  family: f.family, category: f.category, weights: f.weights, source: 'bundled',
}));
const systemSet = EDITOR_FONTS.filter((f) => !f.bundled).map((f) => ({
  family: f.family, category: f.category, weights: f.weights, source: 'system',
}));
const googleSet = GOOGLE_FONTS
  // never shadow a bundled/system family with a network copy
  .filter((g) => !EDITOR_FONTS.some((e) => e.family === g.family))
  .map((g) => ({ ...g, source: 'google' }));

export const FONT_CATALOG = [...bundledSet, ...systemSet, ...googleSet];

export const FONT_CATEGORIES = ['All', 'Sans', 'Serif', 'Display', 'Script', 'Handwritten', 'Fun', 'Mono'];

const byFamily = new Map(FONT_CATALOG.map((f) => [f.family, f]));

export function getFontEntry(family) {
  return byFamily.get(family) || null;
}

export function getFontWeights(family) {
  return byFamily.get(family)?.weights || [400, 700];
}
